import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, Send, Check, X, AlertCircle, 
  Wifi, WifiOff, RefreshCw, QrCode, Phone,
  Clock, CheckCircle, XCircle
} from 'lucide-react';
import whatsappService from '../services/whatsappService';
import dbService from '../services/dbService';
import toast from 'react-hot-toast';

interface ConnectionStatus {
  connected: boolean;
  type: string;
  phoneNumber?: string;
  lastCheck?: Date;
}

const WhatsAppTest: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    connected: false,
    type: 'unknown',
  });
  const [isChecking, setIsChecking] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  
  const [testMessage, setTestMessage] = useState({
    to: '393755292401',
    message: 'Ciao! Questo è un messaggio di test da WhatsApp Flow 🚀',
  });
  const [isSending, setIsSending] = useState(false);
  
  const [recentMessages, setRecentMessages] = useState<any[]>([]);

  // Check connection status
  const checkConnection = async () => {
    setIsChecking(true);
    try {
      const status = await whatsappService.getStatus();
      const connected = status.status === 'connected' && status.authenticated;
      
      setConnectionStatus({
        connected,
        type: 'whatsapp-web',
        lastCheck: new Date(),
      });
      
      // If not connected, try to get QR code
      if (!connected && status.hasQR) {
        const qr = await whatsappService.getQRCode();
        setQrCode(qr);
      }
    } catch (error) {
      setConnectionStatus({
        connected: false,
        type: 'unknown',
        lastCheck: new Date(),
      });
    } finally {
      setIsChecking(false);
    }
  };

  // Load recent messages
  const loadRecentMessages = () => {
    const messages = dbService.getMessages({ limit: 10 });
    setRecentMessages(messages);
  };

  useEffect(() => {
    checkConnection();
    loadRecentMessages();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      loadRecentMessages();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Send test message
  const sendTestMessage = async () => {
    if (!testMessage.to) {
      toast.error('Inserisci un numero di telefono');
      return;
    }

    setIsSending(true);
    try {
      const success = await whatsappService.sendMessage({
        to: testMessage.to.replace(/\D/g, ''),
        message: testMessage.message,
      });

      if (success) {
        toast.success('Messaggio inviato con successo!');
        setTestMessage({ ...testMessage, message: '' });
        loadRecentMessages();
      }
    } catch (error) {
      toast.error('Errore invio messaggio');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-whatsapp-green" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Test WhatsApp API
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Verifica la connessione e invia messaggi di test
                </p>
              </div>
            </div>
            
            <button
              onClick={checkConnection}
              disabled={isChecking}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-dark-bg rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
              <span>Ricontrolla</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Connection Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Stato Connessione
          </h2>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                connectionStatus.connected
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}>
                {connectionStatus.connected ? <Wifi className="w-6 h-6" /> : <WifiOff className="w-6 h-6" />}
              </div>
              
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {connectionStatus.connected ? 'Connesso' : 'Non Connesso'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tipo API: {connectionStatus.type}
                </p>
                {connectionStatus.lastCheck && (
                  <p className="text-xs text-gray-400 mt-1">
                    Ultimo controllo: {connectionStatus.lastCheck.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            
            {connectionStatus.connected && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">API Funzionante</span>
              </div>
            )}
          </div>

          {/* QR Code for Web API */}
          {qrCode && connectionStatus.type === 'web' && !connectionStatus.connected && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-bg rounded-lg text-center">
              <QrCode className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Scansiona il QR code con WhatsApp
              </p>
              <img src={qrCode} alt="QR Code" className="mx-auto" />
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Send Test Message */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Invia Messaggio di Test
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Numero Destinatario
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={testMessage.to}
                    onChange={(e) => setTestMessage({ ...testMessage, to: e.target.value })}
                    placeholder="+39 123 456 7890"
                    className="w-full pl-10 pr-4 py-3 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Messaggio
                </label>
                <textarea
                  value={testMessage.message}
                  onChange={(e) => setTestMessage({ ...testMessage, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50 resize-none"
                  placeholder="Scrivi il tuo messaggio..."
                />
              </div>
              
              <button
                onClick={sendTestMessage}
                disabled={!connectionStatus.connected || isSending}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${
                  connectionStatus.connected && !isSending
                    ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white hover:shadow-lg'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSending ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Invio in corso...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Invia Messaggio</span>
                  </>
                )}
              </button>
            </div>

            {!connectionStatus.connected && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-300">
                    <p className="font-medium">Connessione non attiva</p>
                    <p className="mt-1">Configura le API nelle impostazioni per inviare messaggi.</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Recent Messages */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Messaggi Recenti
              </h2>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentMessages.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  Nessun messaggio inviato
                </p>
              ) : (
                recentMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg ${
                      msg.type === 'sent'
                        ? 'bg-whatsapp-green/10 border border-whatsapp-green/20'
                        : 'bg-gray-100 dark:bg-dark-bg'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 dark:text-white">
                          {msg.content}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                          {msg.status === 'delivered' ? (
                            <Check className="w-3 h-3 text-green-600" />
                          ) : msg.status === 'failed' ? (
                            <X className="w-3 h-3 text-red-600" />
                          ) : (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        msg.type === 'sent'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {msg.type === 'sent' ? 'Inviato' : 'Ricevuto'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* API Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Informazioni API
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Endpoint Webhook
              </h3>
              <p className="text-sm text-gray-900 dark:text-white font-mono bg-gray-100 dark:bg-dark-bg p-2 rounded">
                {window.location.origin}/api/webhook/whatsapp
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Tipo API Configurato
              </h3>
              <p className="text-sm text-gray-900 dark:text-white">
                {connectionStatus.type === 'cloud' && 'WhatsApp Cloud API'}
                {connectionStatus.type === 'official' && 'WhatsApp Business API'}
                {connectionStatus.type === 'web' && 'WhatsApp Web API'}
                {connectionStatus.type === 'unknown' && 'Non configurato'}
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                Limiti API
              </h3>
              <p className="text-sm text-gray-900 dark:text-white">
                1000 messaggi/giorno
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhatsAppTest;
